import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, of as observableOf } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import {
  ManageInsuranceSource,
  ManageInsuranceQueryParams,
} from '@app/appointment/manage-insurance/manage-insurance.component';
import { PrimaryInsuranceAndMembershipIsDpcGraphQLService } from '@app/appointment/primary-insurance-and-membership-is-dpc-graphql.service';
import { FeatureFlags, FeatureFlagVariantsWithOn, FeatureFlagVariants } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import { LinksService } from '@app/core/links.service';

import { PaymentCaptureService } from '../payment-capture/payment-capture.service';
import { PrimaryInsuranceGraphQLService } from './primary-insurance-graphql.service';

export const InsuranceStatus = {
  SELF_PAY: 'Self Pay',
  PENDING: 'Pending',
  VERIFIED: 'Verified',
} as const;
export type InsuranceStatus = typeof InsuranceStatus[keyof typeof InsuranceStatus];

export interface InsuranceCaptureValues {
  variant: FeatureFlagVariantsWithOn;
  primaryInsuranceStatus: string | undefined;
}

export interface InsuranceData {
  insuranceStatus: InsuranceStatus;
  insuranceCompany?: string | null;
  memberId?: string | null;
  copay?: number | null;
}

export interface InsuranceCaptureNavigateParams {
  insuranceCaptureFlowEnabled: boolean;
  source?: ManageInsuranceSource;
  confirmationId?: string;
  replaceUrl?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class InsuranceCaptureService {
  readonly OFF_STATE: InsuranceCaptureValues = {
    variant: FeatureFlagVariantsWithOn.OFF,
    primaryInsuranceStatus: undefined,
  };

  constructor(
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly primaryInsuranceAndMembershipIsDpcGraphQLService: PrimaryInsuranceAndMembershipIsDpcGraphQLService,
    private readonly primaryInsuranceGraphQLService: PrimaryInsuranceGraphQLService,
    private readonly router: Router,
    private readonly links: LinksService,
    private readonly paymentCaptureService: PaymentCaptureService,
  ) {}

  insuranceCaptureEnabled$(isBillableVisit: boolean): Observable<boolean> {
    return this.getInsuranceCaptureValues$(isBillableVisit).pipe(
      map(
        result =>
          result.variant === FeatureFlagVariantsWithOn.ON_VARIANT || result.variant === FeatureFlagVariantsWithOn.ON,
      ),
    );
  }

  getInsuranceCaptureValues$(isBillableVisit: boolean): Observable<InsuranceCaptureValues> {
    if (!isBillableVisit) {
      return observableOf({ variant: FeatureFlagVariantsWithOn.OFF, primaryInsuranceStatus: undefined });
    }

    return this.primaryInsuranceAndMembershipIsDpcGraphQLService.fetch().pipe(
      switchMap(graphqlResult => {
        const primaryInsuranceStatus = graphqlResult.data.patient?.primaryInsurance.verificationStatus;
        const isDpc = graphqlResult.data.membership?.isDpc ?? false;

        if (isDpc) {
          return observableOf({ variant: FeatureFlagVariantsWithOn.OFF, primaryInsuranceStatus });
        }

        return this.insuranceCaptureFlagVariant$().pipe(map(variant => ({ variant, primaryInsuranceStatus })));
      }),
      catchError(() => observableOf({ variant: FeatureFlagVariantsWithOn.OFF, primaryInsuranceStatus: undefined })),
    );
  }

  insuranceCaptureFullPageFlagEnabled$(): Observable<boolean> {
    return this.launchDarklyService
      .featureFlag$<FeatureFlagVariantsWithOn>(
        FeatureFlags.INSURANCE_CAPTURE_WEB_FULL_PAGE,
        FeatureFlagVariantsWithOn.OFF,
      )
      .pipe(map(result => result === FeatureFlagVariantsWithOn.ON_VARIANT || result === FeatureFlagVariantsWithOn.ON));
  }

  insuranceCaptureRequiredFlag$(): Observable<FeatureFlagVariantsWithOn> {
    return this.launchDarklyService.featureFlag$<FeatureFlagVariantsWithOn>(
      FeatureFlags.INSURANCE_CAPTURE_REQUIRED,
      FeatureFlagVariantsWithOn.OFF,
    );
  }

  insuranceCaptureRequired$(): Observable<boolean> {
    return this.launchDarklyService
      .featureFlag$<FeatureFlagVariantsWithOn>(FeatureFlags.INSURANCE_CAPTURE_REQUIRED, FeatureFlagVariantsWithOn.OFF)
      .pipe(map(result => result === FeatureFlagVariantsWithOn.ON_VARIANT || result === FeatureFlagVariantsWithOn.ON));
  }

  getInsuranceCarrierDropdownSearchFlag$(): Observable<FeatureFlagVariantsWithOn> {
    return this.launchDarklyService.featureFlag$<FeatureFlagVariantsWithOn>(
      FeatureFlags.INSURANCE_CAPTURE_CARRIER_DROPDOWN_SEARCH,
      FeatureFlagVariantsWithOn.OFF,
    );
  }

  insuranceCaptureCarrierDropdownSearchFlagEnabled$(): Observable<boolean> {
    return this.getInsuranceCarrierDropdownSearchFlag$().pipe(
      map(result => result === FeatureFlagVariantsWithOn.ON_VARIANT || result === FeatureFlagVariantsWithOn.ON),
    );
  }

  isCarrierDropdownSearchEnabled$(): Observable<boolean> {
    return combineLatest([
      this.insuranceCaptureFullPageFlagEnabled$(),
      this.insuranceCaptureCarrierDropdownSearchFlagEnabled$(),
    ]).pipe(map(([isFullPageEnabled, isDropdownEnabled]) => isFullPageEnabled && isDropdownEnabled));
  }

  insuranceCaptureFlowAfterInventorySelectionEnabled$(
    isBillableVisit: boolean,
    isRemote: boolean,
  ): Observable<boolean> {
    return this.insuranceCaptureFlowAfterInventorySelectionValues$(isBillableVisit, isRemote).pipe(
      map(
        result =>
          result.variant === FeatureFlagVariantsWithOn.ON_VARIANT || result.variant === FeatureFlagVariantsWithOn.ON,
      ),
    );
  }

  insuranceCaptureFlowAfterInventorySelectionValues$(
    isBillableVisit: boolean,
    isRemote: boolean,
  ): Observable<InsuranceCaptureValues> {
    if (!isBillableVisit) {
      return observableOf(this.OFF_STATE);
    }

    return combineLatest([
      this.launchDarklyService.featureFlag$<FeatureFlagVariantsWithOn>(
        FeatureFlags.INSURANCE_CAPTURE_WEB_FULL_PAGE,
        FeatureFlagVariantsWithOn.OFF,
      ),
      this.primaryInsuranceAndMembershipIsDpcGraphQLService.fetch({}, { fetchPolicy: 'network-only' }),
      this.paymentCaptureService.getPaymentCaptureFlagM3Enabled$(),
    ]).pipe(
      map(([flagResult, graphqlResult, paymentCaptureFlagM3Enabled]) => {
        const primaryInsuranceStatus = graphqlResult.data?.patient?.primaryInsurance.verificationStatus;
        const isSelfPay = primaryInsuranceStatus === InsuranceStatus.SELF_PAY;
        const isDpc = graphqlResult.data.membership?.isDpc ?? false;

        if (isDpc) {
          return this.OFF_STATE;
        }

        return {
          variant: isSelfPay || (paymentCaptureFlagM3Enabled && isRemote) ? flagResult : FeatureFlagVariantsWithOn.OFF,
          primaryInsuranceStatus,
        };
      }),
    );
  }

  navigateToInsuranceCaptureIfEnabled(options: InsuranceCaptureNavigateParams) {
    const { insuranceCaptureFlowEnabled, source, confirmationId, replaceUrl } = options;

    if (!insuranceCaptureFlowEnabled) {
      this.router.navigate([this.links.appointmentReview]);
      return;
    }

    this.router.navigate([this.links.manageInsurance], {
      replaceUrl: replaceUrl ?? false,
      queryParams: {
        [ManageInsuranceQueryParams.SOURCE]: source,
        [ManageInsuranceQueryParams.CONFIRMATION_ID]: confirmationId,
      },
    });
  }

  getPrimaryInsurance$(): Observable<InsuranceData> {
    return this.primaryInsuranceGraphQLService.watch({}, { fetchPolicy: 'network-only' }).valueChanges.pipe(
      map(result => {
        const primaryInsurance = result.data?.patient?.primaryInsurance;
        const insuranceStatus = (primaryInsurance?.verificationStatus as InsuranceStatus) || InsuranceStatus.SELF_PAY;

        return {
          insuranceStatus,
          insuranceCompany: primaryInsurance?.name,
          memberId: primaryInsurance?.subscriberNumber,
          copay: primaryInsurance?.copay,
        };
      }),
    );
  }

  getSelfPayEstimatesFlag$(): Observable<FeatureFlagVariants> {
    return this.launchDarklyService.featureFlag$<FeatureFlagVariants>(
      FeatureFlags.SELF_PAY_ESTIMATES,
      FeatureFlagVariants.OFF,
    );
  }

  selfPayEstimatesFlagEnabled$(): Observable<boolean> {
    return this.getSelfPayEstimatesFlag$().pipe(map(result => result === FeatureFlagVariants.ON_VARIANT));
  }

  private insuranceCaptureFlagVariant$(): Observable<FeatureFlagVariantsWithOn> {
    return this.launchDarklyService.featureFlag$<FeatureFlagVariantsWithOn>(
      FeatureFlags.INSURANCE_CAPTURE_WEB_FULL_PAGE,
      FeatureFlagVariantsWithOn.OFF,
    );
  }
}
